import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* eslint-disable @atlaskit/design-system/no-nested-styles */
/* eslint-disable @atlaskit/design-system/prefer-primitives */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react'; // eslint-disable-line @atlaskit/ui-styling-standard/use-compiled
import { useIntl } from 'react-intl-next';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { akEditorFullPageDefaultFontSize, akEditorFullPageDenseFontSize } from '@atlaskit/editor-shared-styles';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithCondition } from '@atlaskit/platform-feature-flags-react';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Text, xcss } from '@atlaskit/primitives';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { buildVisitedNonHyperLinkPayload, INPUT_METHOD } from '../../analytics';
import { cardMessages } from '../../messages';
/**
 * Dynamic padding value that adjusts based on the editor's base font size.
 * When base font size is <= akEditorFullPageDenseFontSize (13px), uses 1px padding.
 * Otherwise, uses default value with token('space.025').
 */
var DYNAMIC_PADDING_BLOCK = "clamp(1px, calc((var(--ak-editor-base-font-size, ".concat(akEditorFullPageDefaultFontSize, "px) - ").concat(akEditorFullPageDenseFontSize, "px) * 999), ", "var(--ds-space-025, 2px)", ")");
var containerStylesOld = css({
  position: 'relative'
});
var containerStyles = css({
  position: 'relative',
  whiteSpace: 'nowrap'
});
var iconWrapperStylesOld = xcss({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '17px',
  width: '17px'
});
var iconWrapperStyles = xcss({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center'
});
var hiddenTextStyle = css({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  position: 'absolute',
  visibility: 'hidden'
});
var linkStylesCommon = xcss({
  position: 'absolute',
  left: 'space.025',
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  paddingBlock: 'space.025',
  paddingInline: 'space.050',
  gap: 'space.025',
  overflow: 'hidden',
  zIndex: 'card',
  backgroundColor: 'color.background.accent.gray.subtlest',
  borderRadius: "var(--ds-radius-small, 3px)",
  color: 'color.text.subtle',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  ':hover': {
    backgroundColor: 'elevation.surface.hovered',
    color: 'color.text.subtle',
    textDecoration: 'none'
  }
});
var linkStylesOld = xcss({
  top: '-1px'
});
var linkStylesHeightFix = xcss({
  top: '0px',
  height: '1.2em'
});
var linkStylesNewWithHeightFix = xcss({
  top: '0px',
  visibility: 'hidden',
  height: '1.2em'
});
var linkStylesVisible = xcss({
  visibility: 'visible'
});
var iconAndLabelStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  gap: 'space.025'
});
var MIN_AVAILABLE_SPACE_WITH_LABEL_OVERLAY = 45;
var ICON_WIDTH = 16;
var DEFAULT_OPEN_TEXT_WIDTH = 28; // Default open text width in English
var MIN_CARD_WIDTH_NOT_COVERED_BY_OVERLAY = 30;
var visitCardLinkAnalytics = function visitCardLinkAnalytics(editorAnalyticsApi, inputMethod) {
  return function (state, dispatch) {
    if (!(state.selection instanceof NodeSelection)) {
      return false;
    }
    var type = state.selection.node.type;
    if (dispatch) {
      var tr = state.tr;
      editorAnalyticsApi === null || editorAnalyticsApi === void 0 || editorAnalyticsApi.attachAnalyticsEvent(buildVisitedNonHyperLinkPayload(type.name, inputMethod))(tr);
      dispatch(tr);
    }
    return true;
  };
};
var HoverLinkOverlayOriginal = function HoverLinkOverlayOriginal(_ref) {
  var children = _ref.children,
    _ref$isVisible = _ref.isVisible,
    isVisible = _ref$isVisible === void 0 ? false : _ref$isVisible,
    url = _ref.url,
    _ref$compactPadding = _ref.compactPadding,
    compactPadding = _ref$compactPadding === void 0 ? false : _ref$compactPadding,
    editorAnalyticsApi = _ref.editorAnalyticsApi,
    view = _ref.view,
    onClick = _ref.onClick,
    _ref$showPanelButton = _ref.showPanelButton,
    showPanelButton = _ref$showPanelButton === void 0 ? false : _ref$showPanelButton,
    showPanelButtonIcon = _ref.showPanelButtonIcon;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var containerRef = useRef(null);
  var hoverLinkButtonRef = useRef(null);
  var hiddenTextRef = useRef(null);
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    showLabel = _useState2[0],
    setShowLabel = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isHovered = _useState4[0],
    setHovered = _useState4[1];
  var openTextWidthRef = useRef(null);
  useLayoutEffect(function () {
    var _containerRef$current, _hoverLinkButtonRef$c;
    if (!isVisible || !isHovered) {
      return;
    }
    var cardWidth = (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : _containerRef$current.offsetWidth;
    var openButtonWidth = (_hoverLinkButtonRef$c = hoverLinkButtonRef.current) === null || _hoverLinkButtonRef$c === void 0 ? void 0 : _hoverLinkButtonRef$c.offsetWidth;
    // Get the hidden text width
    if (!openTextWidthRef.current) {
      var hiddenText = hiddenTextRef.current;
      if (hiddenText) {
        // Measure the width of the hidden text
        // Temporarily make the element visible to measure its width
        hiddenText.style.visibility = 'hidden';
        hiddenText.style.display = 'inline';
        openTextWidthRef.current = hiddenText.offsetWidth;

        // Reset the hiddenText's display property
        hiddenText.style.display = 'none';
        hiddenText.style.visibility = 'inherit';
      } else {
        openTextWidthRef.current = DEFAULT_OPEN_TEXT_WIDTH;
      }
    }
    if (!cardWidth || !openButtonWidth) {
      return;
    }
    var openTextWidth = openTextWidthRef.current || DEFAULT_OPEN_TEXT_WIDTH;
    var canShowLabel = cardWidth - openTextWidth > MIN_AVAILABLE_SPACE_WITH_LABEL_OVERLAY + ICON_WIDTH;
    setShowLabel(canShowLabel);
  }, [isVisible, isHovered]);
  var handleOverlayChange = function handleOverlayChange(isHovered) {
    setHovered(isHovered);
  };
  var sendVisitLinkAnalytics = function sendVisitLinkAnalytics(inputMethod) {
    if (editorAnalyticsApi && view) {
      visitCardLinkAnalytics(editorAnalyticsApi, inputMethod)(view.state, view.dispatch);
    }
  };
  var handleDoubleClick = function handleDoubleClick() {
    if (!showPanelButton) {
      sendVisitLinkAnalytics(INPUT_METHOD.DOUBLE_CLICK);

      // Double click opens the link in a new tab
      window.open(url, '_blank');
    }
  };
  var handleClick = function handleClick(event) {
    if (showPanelButton && onClick) {
      onClick(event);
    } else {
      sendVisitLinkAnalytics(INPUT_METHOD.BUTTON);
    }
  };
  var isPreviewButton = showPanelButton && editorExperiment('platform_editor_preview_panel_linking_exp', true);
  var label = isPreviewButton ? formatMessage(cardMessages.previewButtonTitle) : formatMessage(cardMessages.openButtonTitle);
  var icon = null;
  if (isPreviewButton && showPanelButtonIcon === 'panel') {
    icon = jsx(PanelRightIcon, {
      label: ""
    });
  } else if (isPreviewButton && showPanelButtonIcon === 'modal') {
    icon = jsx(GrowDiagonalIcon, {
      label: ""
    });
  } else {
    icon = jsx(LinkExternalIcon, {
      label: ""
    });
  }
  return jsx("span", {
    ref: containerRef,
    css: containerStylesOld,
    onDoubleClick: handleDoubleClick
    // eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
    ,
    onMouseEnter: function onMouseEnter() {
      return handleOverlayChange(true);
    }
    // eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
    ,
    onMouseLeave: function onMouseLeave() {
      return handleOverlayChange(false);
    }
  }, children, jsx("span", {
    css: hiddenTextStyle,
    "aria-hidden": "true"
  }, jsx(Text, {
    ref: hiddenTextRef,
    size: "small",
    maxLines: 1
  }, label)), isHovered && jsx(Anchor, {
    ref: hoverLinkButtonRef,
    xcss: [linkStylesCommon, expValEquals('platform_editor_hoverlink_ui_fixes_exp', 'cohort', 'css_changes_only') ? linkStylesHeightFix : linkStylesOld],
    href: url,
    target: "_blank",
    style: {
      paddingBlock: compactPadding ? '1px' : expValEquals('cc_editor_ai_content_mode', 'variant', 'test') && fg('platform_editor_content_mode_button_mvp') ? DYNAMIC_PADDING_BLOCK : "var(--ds-space-025, 2px)"
    },
    onClick: handleClick,
    testId: "inline-card-hoverlink-overlay"
  }, jsx(Box, {
    xcss: iconWrapperStylesOld,
    "data-inlinecard-button-overlay": "icon-wrapper-line-height",
    testId: "inline-card-hoverlink-overlay-icon"
  }, icon), showLabel && jsx(Text, {
    size: "small",
    color: "color.text.subtle",
    maxLines: 1,
    testId: "inline-card-hoverlink-overlay-label"
  }, label)));
};
var HoverLinkOverlayNew = function HoverLinkOverlayNew(_ref2) {
  var children = _ref2.children,
    _ref2$isVisible = _ref2.isVisible,
    isVisible = _ref2$isVisible === void 0 ? false : _ref2$isVisible,
    url = _ref2.url,
    _ref2$compactPadding = _ref2.compactPadding,
    compactPadding = _ref2$compactPadding === void 0 ? false : _ref2$compactPadding,
    editorAnalyticsApi = _ref2.editorAnalyticsApi,
    view = _ref2.view,
    onClick = _ref2.onClick,
    _ref2$showPanelButton = _ref2.showPanelButton,
    showPanelButton = _ref2$showPanelButton === void 0 ? false : _ref2$showPanelButton,
    showPanelButtonIcon = _ref2.showPanelButtonIcon;
  var _useIntl2 = useIntl(),
    formatMessage = _useIntl2.formatMessage;
  var containerRef = useRef(null);
  var iconRef = useRef(null);
  var iconLeftRef = useRef(0);
  var iconAndLabelRef = useRef(null);
  var minMaxWidthRef = useRef();
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    showLabel = _useState6[0],
    setShowLabel = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isHovered = _useState8[0],
    setHovered = _useState8[1];
  var _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    showOverlay = _useState0[0],
    setShowOverlay = _useState0[1];
  useLayoutEffect(function () {
    if (!isVisible && !isHovered) {
      return;
    }
    if (!containerRef.current) {
      return;
    }
    if (iconRef.current && iconAndLabelRef.current) {
      var iconRect = iconRef.current.getBoundingClientRect();
      iconLeftRef.current = iconRect.left;
      minMaxWidthRef.current = {
        min: iconRect.width,
        max: iconAndLabelRef.current.getBoundingClientRect().width
      };
    }
    if (!iconLeftRef.current || !minMaxWidthRef.current) {
      return;
    }
    var containerDomRect = containerRef.current.getBoundingClientRect();
    var containerStyles = getComputedStyle(containerRef.current);
    var containerHeight = containerDomRect.height - parseFloat(containerStyles.paddingTop) - parseFloat(containerStyles.paddingBottom) - parseFloat(containerStyles.borderTopWidth) - parseFloat(containerStyles.borderBottomWidth);
    var oneLine = parseFloat(containerStyles.lineHeight) >= containerHeight;
    var firstLineWidth = containerDomRect.right - iconLeftRef.current;

    // We don't want HoverLinkOverlay to cover the entire card if it is oneline. It allows the user to click on the card itself.
    var reservedWidth = oneLine ? MIN_CARD_WIDTH_NOT_COVERED_BY_OVERLAY : 0;
    var availableWidth = firstLineWidth - reservedWidth;

    // True when icon and label can be shown
    setShowLabel(availableWidth >= minMaxWidthRef.current.max);
    // True when at least an icon can be shown
    setShowOverlay(availableWidth >= minMaxWidthRef.current.min);
  }, [isVisible, isHovered]);
  var handleOverlayChange = function handleOverlayChange(isHovered) {
    setHovered(isHovered);
  };
  var sendVisitLinkAnalytics = function sendVisitLinkAnalytics(inputMethod) {
    if (editorAnalyticsApi && view) {
      visitCardLinkAnalytics(editorAnalyticsApi, inputMethod)(view.state, view.dispatch);
    }
  };
  var handleDoubleClick = function handleDoubleClick() {
    if (!showPanelButton) {
      sendVisitLinkAnalytics(INPUT_METHOD.DOUBLE_CLICK);

      // Double click opens the link in a new tab
      window.open(url, '_blank');
    }
  };
  var handleClick = function handleClick(event) {
    if (showPanelButton && onClick) {
      onClick(event);
    } else {
      sendVisitLinkAnalytics(INPUT_METHOD.BUTTON);
    }
  };
  var isPreviewButton = showPanelButton && editorExperiment('platform_editor_preview_panel_linking_exp', true);
  var label = isPreviewButton ? formatMessage(cardMessages.previewButtonTitle) : formatMessage(cardMessages.openButtonTitle);
  var icon = null;
  if (isPreviewButton && showPanelButtonIcon === 'panel') {
    icon = jsx(PanelRightIcon, {
      label: ""
    });
  } else if (isPreviewButton && showPanelButtonIcon === 'modal') {
    icon = jsx(GrowDiagonalIcon, {
      label: ""
    });
  } else {
    icon = jsx(LinkExternalIcon, {
      label: ""
    });
  }
  return jsx("span", {
    ref: containerRef,
    css: containerStyles,
    onDoubleClick: handleDoubleClick,
    onMouseEnter: function onMouseEnter() {
      return handleOverlayChange(true);
    },
    onMouseLeave: function onMouseLeave() {
      return handleOverlayChange(false);
    },
    role: "presentation" // remove as part of platform_editor_hoverlink_ui_fixes_exp cleanup
    ,
    onFocus: function onFocus() {} // remove as part of platform_editor_hoverlink_ui_fixes_exp cleanup
    ,
    onBlur: function onBlur() {} // remove as part of platform_editor_hoverlink_ui_fixes_exp cleanup
  }, isHovered && jsx(Anchor, {
    xcss: [linkStylesCommon, linkStylesNewWithHeightFix, showOverlay && linkStylesVisible],
    href: url,
    target: "_blank",
    style: {
      paddingBlock: compactPadding ? '1px' : expValEquals('cc_editor_ai_content_mode', 'variant', 'test') && fg('platform_editor_content_mode_button_mvp') ? DYNAMIC_PADDING_BLOCK : "var(--ds-space-025, 2px)"
    },
    onClick: handleClick,
    testId: "inline-card-hoverlink-overlay"
  }, jsx(Box, {
    as: "span",
    xcss: iconAndLabelStyles,
    ref: iconAndLabelRef
  }, jsx(Box, {
    ref: iconRef,
    as: "span",
    xcss: iconWrapperStyles,
    "data-inlinecard-button-overlay": "icon-wrapper-line-height",
    testId: "inline-card-hoverlink-overlay-icon"
  }, icon), (showLabel || !minMaxWidthRef.current) && jsx(Text, {
    size: "small",
    color: "color.text.subtle",
    maxLines: 1,
    testId: "inline-card-hoverlink-overlay-label"
  }, label))), children);
};
var HoverLinkOverlay = componentWithCondition(function () {
  return expValEquals('platform_editor_hoverlink_ui_fixes_exp', 'cohort', 'css_js_changes');
}, HoverLinkOverlayNew, HoverLinkOverlayOriginal);
export default HoverLinkOverlay;